import AppButton from '@/components/atoms/AppButton';
import { EditFill } from '@/components/atoms/AppIcon';
import { useAdminTheme } from '@/shared/providers/admin-theme/AdminThemeContext';
import type { RezeptLimit } from '@/types/admin-rezept-limit';
import { convGtJDate, convGtJMonth } from '@/utils/dateConverter';
import AppTable, { type AppTableProps } from '@/components/atoms/AppTable';
import type { SorterResult } from 'antd/es/table/interface';
import { Empty } from 'antd';
import { EMPTY_TEXT_FILTERED } from '@/constants';

interface RezeptLimitTableProps {
  data: RezeptLimit[];
  sortInfo?: { columnKey?: string; order?: 'ascend' | 'descend' };
  isLoading?: boolean;
  onTableChange?: (sorter: SorterResult<RezeptLimit> | SorterResult<RezeptLimit>[]) => void;
  onEdit?: (record: RezeptLimit) => void;
}

const formatYearMonthWareki = (yearmonth: number): string => {
  const str = String(yearmonth);
  if (str.length !== 6) return str;
  const yyyy = str.slice(0, 4);
  const mm = str.slice(4, 6);
  return convGtJMonth(`${yyyy}-${mm}-01`) || `${yyyy}年${mm}月`;
};

const formatDateWareki = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return convGtJDate(dateStr) || dateStr;
};

const RezeptLimitTable = ({
  data,
  sortInfo,
  isLoading,
  onTableChange,
  onEdit,
}: RezeptLimitTableProps) => {
  const { colors } = useAdminTheme();
  const btnStyle = { backgroundColor: colors.primary, color: '#fff', borderColor: colors.primary };

  const getSortOrder = (key: string) => {
    return sortInfo?.columnKey === key ? sortInfo.order : undefined;
  };

  const columns: AppTableProps<RezeptLimit>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '10%',
    },
    {
      title: '提出年月',
      dataIndex: 'rezept_yearmonth',
      key: 'rezept_yearmonth',
      width: '35%',
      sorter: true,
      sortOrder: getSortOrder('rezept_yearmonth'),
      render: (_, record) => formatYearMonthWareki(record.rezept_yearmonth),
    },
    {
      title: '提出期限',
      dataIndex: 'limit_date',
      key: 'limit_date',
      width: '35%',
      sorter: true,
      sortOrder: getSortOrder('limit_date'),
      render: (_, record) => formatDateWareki(record.limit_date),
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <AppButton icon={<EditFill />} style={btnStyle} onClick={() => onEdit?.(record)}>
          編集
        </AppButton>
      ),
    },
  ];

  return (
    <AppTable<RezeptLimit>
      columns={columns}
      dataSource={data}
      rowKey="id"
      isAdminSite
      loading={isLoading}
      onChange={(_, __, sorter) => onTableChange?.(sorter as SorterResult<RezeptLimit>)}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />,
      }}
    />
  );
};

export default RezeptLimitTable;
